import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { TAAL_OPSLAG, vertaal, type Sleutel, type TaalCode } from "./vertalingen";

type TaalContextWaarde = {
  taal: TaalCode;
  zetTaal: (taal: TaalCode) => void;
  t: <K extends Sleutel>(sleutel: K) => ReturnType<typeof vertaal<K>>;
};

const TaalContext = createContext<TaalContextWaarde | null>(null);

export function TaalProvider({ children }: { children: ReactNode }) {
  const [taal, setTaalState] = useState<TaalCode>("nl");

  useEffect(() => {
    try {
      const opgeslagen = window.localStorage.getItem(TAAL_OPSLAG);
      if (opgeslagen === "nl" || opgeslagen === "en") setTaalState(opgeslagen);
    } catch {
      // localStorage niet beschikbaar: standaard Nederlands.
    }
  }, []);

  const zetTaal = useCallback((nieuw: TaalCode) => {
    setTaalState(nieuw);
    try {
      window.localStorage.setItem(TAAL_OPSLAG, nieuw);
    } catch {
      // Keuze kan niet bewaard worden; de sessie zelf werkt gewoon.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = taal;
  }, [taal]);

  const waarde = useMemo<TaalContextWaarde>(
    () => ({
      taal,
      zetTaal,
      t: (sleutel) => vertaal(taal, sleutel),
    }),
    [taal, zetTaal],
  );

  return <TaalContext.Provider value={waarde}>{children}</TaalContext.Provider>;
}

export function useTaal(): TaalContextWaarde {
  const ctx = useContext(TaalContext);
  if (ctx) return ctx;
  return {
    taal: "nl",
    zetTaal: () => {},
    t: (sleutel) => vertaal("nl", sleutel),
  };
}
